import React, { useContext, useState } from "react";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import { useTenant } from "../../../../Context/Tenant/TenantContext";
import Scrollable from "../../../Shared/Edit/Scrollable";
import { statusHelper } from "../utils";
import {
    SET_SELECTED_TENANT_ID,
    TOGGLE_SHOW_EDIT_TENANT_DIALOG,
    UPDATE_TENANT,
    UPDATE_TENANT_APP,
    TOGGLE_SHOW_ADD_APP_DIALOG,
    SET_SELECTED_MASTER_PROJECT_ID,
    TOGGLE_SHOW_DELETE_TENANT_APP_DIALOG,
    SET_NOTIFICATION_MESSAGE,
    TOGGLE_SHOW_NOTIFICATION,
    SET_NOTIFICATION_TYPE
} from "../TenantEvents";
import UserDisplay from "../../../Shared/User/UserDisplay";
import { resentInvitation } from "../../../../API/Tenant/Tenant";
import InnerLoader from "../../../Shared/Loader/InnerLoader";
import { OverlayLoader } from "../../../Shared/Loader/OverlayLoader";
import { GlobalContext } from "../../../../Renderers/GlobalContext";
import { useTranslation } from "react-i18next";
import { sendEmail } from "../../../../API/EmailApi";

// ✅ Status Cell Component 
const StatusCell = ({ dataItem }) => {
    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const status = statusHelper.find(status => status.key === dataItem.status.toLowerCase()) || statusHelper.find(status => status.key === "notfound");
    const statusText = t(status.key);
    return (
        <td title={status.value}>
            <span className={`text-${status.color}`}>
                <i className={status.icon}></i>
                <span className="ms-2">{statusText}</span>
            </span>
        </td>
    );
};

// ✅ Action Cell Component
const ActionCell = ({ dataItem }) => {
    const { dispatch } = useTenant();

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const handleAddAppClick = () => {
        dispatch({ type: SET_SELECTED_TENANT_ID, payload: dataItem._id });
        dispatch({ type: TOGGLE_SHOW_ADD_APP_DIALOG });
    };

    const handleEditClick = () => {
        dispatch({ type: SET_SELECTED_TENANT_ID, payload: dataItem._id });
        dispatch({ type: TOGGLE_SHOW_EDIT_TENANT_DIALOG });
    };

    return (
        <td className="text-end pe-2">
            <button
                title={t("edit_tenant")}
                className="ms-2 btn btn-secondary cursor-pointer"
                onClick={handleEditClick}
                aria-label="Edit Tenant"
            >
                <i className="fa fa-edit"></i>
            </button>
            <button
                title={t("add_new_app")}
                onClick={handleAddAppClick}
                className="btn btn-primary ms-2 cursor-pointer"
                aria-label={t("add_new_app")}
            >
                <i className="fa fa-plus me-2"></i>
                {t("new_app")}
            </button>
        </td>
    );
};

// ✅ Detail Component
const TenantAppsGrid = ({ dataItem }) => {
    const { subscription, user } = useContext(GlobalContext).state
    const { state, dispatch } = useTenant();
    const [inProgress, setInProgress] = useState(false);

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    if (!dataItem.expanded) return null;

    const handleResendClick = async (masterProjectId) => {
        setInProgress(true);
        try {
            const response = await resentInvitation(dataItem._id, masterProjectId, subscription._id);
            if (response._id) {
                dispatch({
                    type: UPDATE_TENANT_APP,
                    payload: {
                        id: dataItem._id,
                        masterProjectId: masterProjectId,
                        data: { status: "invited" }
                    }
                });
                dispatch({ type: SET_NOTIFICATION_MESSAGE, payload: "Invitation resent successfully" });
                dispatch({ type: TOGGLE_SHOW_NOTIFICATION });
                dispatch({ type: SET_NOTIFICATION_TYPE, payload: "success" });
                const firstName = user.firstName || user.email.split('@')[0];
                const lastName = user.lastName || "";
                const invitedUserFirstName = dataItem.tenantUser.firstName || dataItem.tenantEmail.split('@')[0];
                const designerUrl = dataItem.tenantOrganization.orgName ? `${process.env.REACT_APP_DESIGNER_URL}` : process.env.REACT_APP_DESIGNER_URL + "/signup";

                const templateProperties = {
                    subject: "Invitation to Add a New App to Your Organization",
                    invitedUserFirstName,
                    firstName,
                    lastName,
                    designerUrl,
                }
                await sendEmail(dataItem.tenantEmail, process.env.REACT_APP_SENDGGRID_INVITE_TENANT_OWNER_TO_APP_KEY, templateProperties);
            } else {
                if (response.type === "sent") {
                    dispatch({
                        type: UPDATE_TENANT_APP,
                        payload: {
                            id: dataItem._id,
                            masterProjectId: masterProjectId,
                            data: { status: "invited" }
                        }
                    });
                }
                if (response.message) {
                    dispatch({ type: SET_NOTIFICATION_MESSAGE, payload: response.message });
                    dispatch({ type: TOGGLE_SHOW_NOTIFICATION });
                    dispatch({ type: SET_NOTIFICATION_TYPE, payload: "error" });
                }
            }
        } catch (error) {
            console.error("Failed to resend invitation:", error);
        } finally {
            setInProgress(false);
        }
    };

    const handleDeleteClick = async (masterProjectId) => {
        dispatch({ type: SET_SELECTED_TENANT_ID, payload: dataItem._id });
        dispatch({ type: SET_SELECTED_MASTER_PROJECT_ID, payload: masterProjectId });
        dispatch({ type: TOGGLE_SHOW_DELETE_TENANT_APP_DIALOG });
    }

    return (
        <div className="my-3">
            <Grid data={dataItem.masterProjects}>
                <GridNoRecords>
                    <small className="text-muted text-center">
                        {t("no_apps_inviations")}
                    </small>

                </GridNoRecords>
                <Column
                    width={50}
                    title="#"
                    cell={({ dataIndex }) => (
                        <td>{dataIndex + 1}</td>
                    )}
                />
                <Column
                    field="masterProjectId"
                    title={t("app")}
                    cell={({ dataItem }) => {
                        const project = state.projects.find(project => project._id === dataItem.masterProjectId);
                        return (
                            <td title={project?.name || ""}>
                                {project?.name || ""}
                            </td>
                        );
                    }}
                />
                <Column
                    field="masterProjectId"
                    title={t("Environment")}
                    cell={({ dataItem }) => {
                        const project = state.projects.find(project => project._id === dataItem.masterProjectId);
                        const environment = project?.instances.find(instance => instance._id === dataItem.masterAppId);
                        return (
                            <td title={environment?.instanceName || ""}>
                                {environment?.instanceName || ""}
                            </td>
                        );
                    }}
                />
                <Column
                    field="status"
                    title={t("status")}
                    cell={StatusCell} />
                <Column
                    width={250}
                    title={t("created_on")}
                    cell={props => {
                        if (props.dataItem.tenantProjectCreatedDate && props.dataItem.tenantProjectCreatedDate !== "Invalid Date") {
                            return (
                                <td>{new Date(props.dataItem.tenantProjectCreatedDate).toLocaleString()}</td>
                            );
                        }
                        return (
                            <td>
                                <span className="text-muted">
                                    {t("app_not_created_yet")}
                                </span>
                            </td>
                        );
                    }}
                />
                <Column
                    cell={({ dataItem }) => (
                        <td className="text-end">
                            {inProgress && <InnerLoader borderColor="green" />}
                            {inProgress && <OverlayLoader />}
                            {(dataItem.status === "invited" || dataItem.status === "rejected" || dataItem.status === "clone_failed") && (
                                <>
                                    <button
                                        title={t("resend_invitation")}
                                        disabled={inProgress || dataItem.status === "invited"}
                                        className="btn btn-secondary cursor-pointer"
                                        onClick={() => handleResendClick(dataItem.masterProjectId)}
                                        aria-label={t("resend_invitation")}
                                    >
                                        <i className="fal fa-envelope"></i>
                                    </button>
                                    <button
                                        title={t("delete_app_invitation")}
                                        disabled={inProgress}
                                        className="btn btn-danger ms-2 cursor-pointer"
                                        onClick={() => handleDeleteClick(dataItem.masterProjectId)}
                                        aria-label={t("delete_app_invitation")}
                                    >
                                        <i className="fal fa-trash"></i>
                                    </button>
                                </>
                            )}
                        </td>
                    )}
                />
            </Grid>
        </div>
    )

}

const TenantGrid = () => {
    const { state, dispatch } = useTenant();
    const { tenants, searchValue } = state;

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })


    let filteredTenants = tenants.filter(tenant =>
        tenant.tenantName.toLowerCase().includes(searchValue.toLowerCase()) ||
        tenant.tenantEmail.toLowerCase().includes(searchValue.toLowerCase())
    )

    const expandChange = (event) => {
        const dataItem = event.dataItem;
        dispatch({
            type: UPDATE_TENANT,
            payload: {
                id: dataItem._id,
                data: { expanded: !dataItem.expanded }
            }
        })
    }

    return (
        <Scrollable>
            <div className="m-9 my-3 checkRuntimeUser">
                <Grid
                    reorderable
                    data={filteredTenants}
                    detail={TenantAppsGrid}
                    expandField="expanded"
                    onExpandChange={expandChange}
                >
                    <GridNoRecords>
                        <small className="text-muted text-center">
                            {t("no_tenants_found")}
                        </small>
                    </GridNoRecords>
                    <Column
                        width={50}
                        title="#"
                        cell={({ dataIndex }) => (
                            <td>{dataIndex + 1}</td>
                        )}
                    />
                    <Column
                        title={t("tenant_name")}
                        field="tenantName"
                        cell={({ dataItem }) => (
                            <td title={dataItem.tenantName}>{dataItem.tenantName}</td>
                        )}
                    />
                    <Column
                        title={t("organization")}
                        cell={({ dataItem }) => (
                            <td title={dataItem.tenantOrganization?.orgName || ""}>{dataItem.tenantOrganization?.orgName || "Pending User Registration"}</td>
                        )}
                    />
                    <Column
                        title={t("owner")}
                        field="tenantEmail"
                        cell={({ dataItem }) =>
                            <td>
                                <UserDisplay
                                    bgColor={dataItem.tenantUser.bgColor || "#000"}
                                    email={dataItem.tenantUser.email || dataItem.tenantEmail}
                                    firstName={dataItem.tenantUser.firstName || ""}
                                    lastName={dataItem.tenantUser.lastName || ""}
                                    displayName={dataItem.tenantUser.displayName || ""}
                                    showEmail={dataItem.tenantUser.email ? true : false}
                                />
                            </td>
                        }
                    />
                    <Column
                        width={90}
                        title={t("total_users")}
                        cell={({ dataItem }) => (
                            <td>{dataItem.tenantOrganization?.users?.length || 0}</td>
                        )}
                    />
                    <Column
                        width={200}
                        cell={ActionCell}
                    />
                </Grid>
            </div>
        </Scrollable>
    );
};

export default TenantGrid;
